// SA-3
import type { ProductionCalendarDayRecord, ShiftRecord } from '../types/records'
import { isPositionShift5_2 } from './productionWorkers'
import { isRotationShift, isShift5_2 } from './shifts'

/** YYYY-MM-DD в локальной зоне (для фильтров PocketBase date). */
export function toDateKey(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function parseDateKey(key: string): Date {
  const [y, m, d] = key.split('-').map(Number)
  return new Date(y, m - 1, d)
}

export function startOfMonth(year: number, month: number): Date {
  return new Date(year, month, 1)
}

export function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate()
}

/** Понедельник = 0 … Воскресенье = 6. */
export function weekdayMon0(d: Date): number {
  return (d.getDay() + 6) % 7
}

export const WEEKDAY_LABELS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'] as const

export const MONTH_LABELS = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
] as const

/** День без записи в календаре: цех работает (график 2/2, включая сб/вс). */
export function defaultIsWorking(_d: Date): boolean {
  return true
}

export function isWeekdayMonFri(d: Date): boolean {
  return weekdayMon0(d) < 5
}

export function buildCalendarRecordMap(
  records: ProductionCalendarDayRecord[],
): Map<string, ProductionCalendarDayRecord> {
  const map = new Map<string, ProductionCalendarDayRecord>()
  for (const r of records) {
    const key = normalizePbDate(r.date)
    const brigadeId = normalizeRelationId(r.workingBrigade)
    map.set(key, {
      ...r,
      workingBrigade: brigadeId || undefined,
    })
  }
  return map
}

export function resolvePlantIsWorking(
  dateKey: string,
  calendarMap: Map<string, Pick<ProductionCalendarDayRecord, 'isWorking'>>,
): boolean {
  const rec = calendarMap.get(dateKey)
  if (rec) return rec.isWorking
  return defaultIsWorking(parseDateKey(dateKey))
}

/** Карта календаря с полем workingBrigade (для смен 2/2). */
export type CalendarBrigadeMap = Map<
  string,
  Pick<ProductionCalendarDayRecord, 'isWorking' | 'workingBrigade'>
>

function resolveWorkingBrigadeLocal(
  dateKey: string,
  calendarMap: CalendarBrigadeMap,
): string | null {
  if (!resolvePlantIsWorking(dateKey, calendarMap)) return null
  const rec = calendarMap.get(dateKey)
  const brigade = normalizeRelationId(rec?.workingBrigade)
  return brigade || null
}

export function* iterateDateKeys(fromKey: string, toKey: string): Generator<string> {
  const from = parseDateKey(fromKey)
  const to = parseDateKey(toKey)
  const d = new Date(from)
  while (d.getTime() <= to.getTime()) {
    yield toDateKey(d)
    d.setDate(d.getDate() + 1)
  }
}

export function countPlantWorkingDaysInRange(
  fromKey: string,
  toKey: string,
  calendarMap: Map<string, Pick<ProductionCalendarDayRecord, 'isWorking'>>,
): number {
  let count = 0
  for (const key of iterateDateKeys(fromKey, toKey)) {
    if (resolvePlantIsWorking(key, calendarMap)) count++
  }
  return count
}

export function calendarDayLabel(isWorking: boolean): string {
  return isWorking ? 'Рабочий' : 'Простой'
}

/** Подпись дня в сетке календаря с учётом сб/вс при работе 2/2. */
export function calendarDayDetailLabel(isWorking: boolean, dateKey: string): string {
  if (!isWorking) return 'Простой / праздник'
  return isWeekdayMonFri(parseDateKey(dateKey)) ? 'Рабочий' : 'Рабочий (сб/вс)'
}

/**
 * Рабочий ли день для сотрудника: простой цеха → нет;
 * смена 5/2 или должность из 5/2 → только пн–пт;
 * смены 1/2 → день бригады в календаре или замена (запись табеля на workingBrigade).
 */
export function isWorkerWorkDay(
  dateKey: string,
  worker: { id?: string; defaultShift?: string | null; position?: string | null },
  shiftsById: Map<string, Pick<ShiftRecord, 'name'>>,
  calendarMap: CalendarBrigadeMap,
  timesheetShiftByWorker?: Map<string, string>,
): boolean {
  if (!resolvePlantIsWorking(dateKey, calendarMap)) return false

  const shiftId = worker.defaultShift || ''
  if (shiftId) {
    const shift = shiftsById.get(shiftId)
    if (shift && isShift5_2(shift)) {
      return isWeekdayMonFri(parseDateKey(dateKey))
    }
  }

  if (isPositionShift5_2(worker.position || '')) {
    return isWeekdayMonFri(parseDateKey(dateKey))
  }

  const workingBrigade = resolveWorkingBrigadeLocal(dateKey, calendarMap)
  if (!workingBrigade) return false

  if (shiftId) {
    const shift = shiftsById.get(shiftId)
    if (shift && isRotationShift(shift)) {
      if (shiftId === workingBrigade) return true
      const entryShift = worker.id ? timesheetShiftByWorker?.get(worker.id)?.trim() : undefined
      return entryShift === workingBrigade
    }
  }

  const entryShift = worker.id ? timesheetShiftByWorker?.get(worker.id)?.trim() : undefined
  if (entryShift === workingBrigade) return true

  return shiftId === workingBrigade
}

/** Нормализация date из PocketBase (может быть YYYY-MM-DD или ISO). */
export function normalizePbDate(value: string): string {
  return value.slice(0, 10)
}

/** Фильтр PocketBase для поля date (значения хранятся с временем). */
export function calendarDateRangeFilter(fromKey: string, toKey: string): string {
  return `date >= "${fromKey} 00:00:00" && date <= "${toKey} 23:59:59"`
}

/** ID relation-поля (строка или expand-объект). */
export function normalizeRelationId(value: unknown): string {
  if (typeof value === 'string') return value.trim()
  if (value && typeof value === 'object' && 'id' in value) {
    const id = (value as { id?: unknown }).id
    return typeof id === 'string' ? id.trim() : ''
  }
  return ''
}
